package com.eladfinish.bible.contest.screens.search.ui

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.common.gender.personalizeGender
import com.eladfinish.bible.contest.screens.search.model.VerseUiModel
import com.eladfinish.bible.contest.screens.search.model.countDifferentBooks
import com.eladfinish.bible.contest.screens.search.mvi.AutocompleteUiState
import com.eladfinish.bible.contest.screens.search.mvi.SearchAction
import com.eladfinish.bible.contest.screens.search.mvi.SearchState
import com.eladfinish.bible.contest.screens.search.suggestions.PopularSearchesFilter
import com.eladfinish.bible.contest.screens.search.suggestions.SuggestionsPage
import com.eladfinish.bible.contest.screens.search.usecases.GetSpecialSignsUseCase
import com.eladfinish.bible.contest.type_expression_to_search
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import com.eladfinish.bible.contest.ui.extensions.LocalNavigationBarInset
import com.eladfinish.bible.contest.ui.layout.isShortWideWindow
import com.eladfinish.bible.contest.ui.layout.readableWidth
import dev.icerock.moko.resources.compose.localized
import dev.icerock.moko.resources.desc.desc
import kotlinx.collections.immutable.persistentListOf
import kotlinx.coroutines.flow.drop

@Composable
fun SearchScreenContent(
    state: SearchState,
    signsScrollState: ScrollState,
    onAction: (SearchAction) -> Unit,
    modifier: Modifier = Modifier,
) {
    val controls: @Composable () -> Unit = {
        SearchButtonContent(
            isButtonSearchEnabled = state.isSearchEnabled,
            shouldShowAiSearch = state.shouldShowAiSearch,
            onSearchClick = {
                onAction(SearchAction.SearchClicked)
            },
            onSearchLongClick = {
                onAction(SearchAction.SearchLongClicked)
            },
            onAiSearchClick = {
                onAction(SearchAction.AiSearchClicked)
            },
            onHorizontalArrowClick = { direction ->
                onAction(SearchAction.HorizontalArrowClicked(direction))
            },
            onHorizontalArrowLongClick = { direction ->
                onAction(SearchAction.HorizontalArrowLongClicked(direction))
            },
        )

        SearchInput(
            inputLabel = SharedRes.strings.type_expression_to_search.desc().personalizeGender(state.hebrewGender),
            expression = state.textFieldValue,
            isExpanded = state.isNikudAndTeamimSectionVisible,
            errorMessage = state.errorMessage,
            onDone = {
                onAction(SearchAction.SearchClicked)
            },
            onValueChange = { textFieldValue ->
                onAction(SearchAction.TextFieldValueChanged(textFieldValue))
            },
            onArrowClick = {
                onAction(SearchAction.ToggleNikudAndTeamimSection)
            },
            onArrowLongClick = {
                onAction(SearchAction.NikudAndTeamimSectionLongClicked)
            },
            onClearClick = {
                onAction(SearchAction.ClearClicked)
            },
            onClearLongClick = {
                onAction(SearchAction.ClearLongClicked)
            },
        )

        AnimatedVisibility(
            visible = state.isNikudAndTeamimSectionVisible,
        ) {
            NikudAndTeamimContainer(
                signs = state.specialSigns,
                scrollState = signsScrollState,
                onSignClick = { sign ->
                    onAction(SearchAction.SignClicked(sign))
                },
                onSignLongClick = { sign ->
                    onAction(SearchAction.SignLongClicked(sign))
                },
            )
        }
    }

    if (isShortWideWindow()) {
        // The controls beside the results: above them, they would leave the results no height at all.
        Row(
            modifier = modifier
                .fillMaxSize()
                .padding(8.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            Column(
                modifier = Modifier
                    .weight(0.45f)
                    .fillMaxHeight()
                    .verticalScroll(rememberScrollState()),
            ) {
                controls()
            }
            SearchPages(
                state = state,
                onAction = onAction,
                modifier = Modifier
                    .weight(0.55f)
                    .fillMaxHeight(),
            )
        }
    } else {
        Column(
            modifier = modifier
                .fillMaxSize()
                .readableWidth(max = 840.dp)
                .padding(8.dp),
        ) {
            controls()

            Spacer(
                modifier = Modifier.height(8.dp),
            )

            SearchPages(
                state = state,
                onAction = onAction,
                modifier = Modifier.weight(1f),
            )
        }
    }
}

/** The results and, when suggestions are on, the pages of popular searches beside them. */
@Composable
private fun SearchPages(
    state: SearchState,
    onAction: (SearchAction) -> Unit,
    modifier: Modifier = Modifier,
) {
    if (!state.isSearchSuggestionsEnabled) {
        ResultsPage(state = state, onAction = onAction, modifier = modifier)
        return
    }

    Column(modifier = modifier) {

        // Three pages under the nikud bar, swipeable, picked by the chips below them. The pager
        // scrolls horizontally, each page's list vertically; nested scrolling keeps them apart.
        val pagerState = rememberPagerState(initialPage = state.suggestionsPage.ordinal) { SuggestionsPage.entries.size }

        // The offset too: a rotation during the move ends it, and leaves the pager between two pages.
        LaunchedEffect(state.suggestionsPage) {
            if (pagerState.currentPage != state.suggestionsPage.ordinal || pagerState.currentPageOffsetFraction != 0f) {
                pagerState.animateScrollToPage(state.suggestionsPage.ordinal)
            }
        }

        // drop(1): the page it starts on is not a choice of the user. After a rotation during a
        // move it is the page being left, and would replace the one the store is moving to.
        LaunchedEffect(pagerState) {
            snapshotFlow { pagerState.settledPage }.drop(1).collect { page ->
                onAction(SearchAction.SuggestionsPageSelected(SuggestionsPage.entries[page]))
            }
        }

        HorizontalPager(
            state = pagerState,
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f),
            beyondViewportPageCount = 1,
        ) { pageIndex ->
            // The chips below take the navigation bar; the pages end above them.
            CompositionLocalProvider(LocalNavigationBarInset provides 0.dp) {
                when (SuggestionsPage.entries[pageIndex]) {
                    SuggestionsPage.RESULTS -> ResultsPage(state = state, onAction = onAction)

                    SuggestionsPage.HOT_NOW -> PopularSearchesPage(
                        rows = state.popularLists?.hotNow.orEmpty(),
                        onSuggestionClick = { onAction(SearchAction.AutocompleteSuggestionClicked(it, SuggestionsPage.HOT_NOW)) },
                        onFillClick = { onAction(SearchAction.AutocompleteSuggestionFillClicked(it, SuggestionsPage.HOT_NOW)) },
                        onMoreClick = { onAction(SearchAction.MoreSuggestionsClicked(PopularSearchesFilter.HOT_NOW)) },
                    )

                    SuggestionsPage.MOST_POPULAR -> PopularSearchesPage(
                        rows = state.popularLists?.mostPopular.orEmpty(),
                        onSuggestionClick = { onAction(SearchAction.AutocompleteSuggestionClicked(it, SuggestionsPage.MOST_POPULAR)) },
                        onFillClick = { onAction(SearchAction.AutocompleteSuggestionFillClicked(it, SuggestionsPage.MOST_POPULAR)) },
                        onMoreClick = { onAction(SearchAction.MoreSuggestionsClicked(PopularSearchesFilter.ALL_TIME)) },
                    )
                }
            }
        }

        SuggestionsPageChips(
            selected = state.suggestionsPage,
            recentDays = state.popularLists?.recentDays ?: 7,
            onSelected = { onAction(SearchAction.SuggestionsPageSelected(it)) },
        )
    }
}

/** The results of the last search, or the autocomplete while the user types. */
@Composable
private fun ResultsPage(
    state: SearchState,
    onAction: (SearchAction) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier.fillMaxSize(),
    ) {
        val autocomplete = state.autocomplete
        if (autocomplete is AutocompleteUiState.ForPrefix) {
            AutocompleteSuggestionsContent(
                state = autocomplete,
                onSuggestionClick = { onAction(SearchAction.AutocompleteSuggestionClicked(it, SuggestionsPage.RESULTS)) },
                onFillClick = { onAction(SearchAction.AutocompleteSuggestionFillClicked(it, SuggestionsPage.RESULTS)) },
                onMoreClick = { onAction(SearchAction.MoreSuggestionsClicked(PopularSearchesFilter.ALL_TIME)) },
            )
            return@Column
        }

        // Nothing to summarize before the first search: the bare "search results" label was noise.
        if (state.hasSearchSummary) {
            val booksCount = remember(state.results) { state.results.countDifferentBooks() }

            SearchResultsSummary(
                summary = state.resultsSummary.localized(),
                booksCount = booksCount,
                onBooksClick = {
                    onAction(SearchAction.ResultsByBookClicked)
                },
            )

            Spacer(
                modifier = Modifier.height(4.dp),
            )
        }

        SearchResultsList(
            items = state.results,
            onItemClick = { item ->
                onAction(SearchAction.ResultClicked(item))
            },
            onItemLongClick = { item ->
                onAction(SearchAction.ResultLongClicked(item))
            },
            jump = state.resultsJump,
            onJumpHandled = {
                onAction(SearchAction.ResultsJumpHandled)
            },
        )
    }
}

@Preview(showBackground = true)
@Composable
private fun SearchScreenContentPreview() {
    PreviewTheme {
        SearchScreenContent(
            state = SearchState(
                specialSigns = GetSpecialSignsUseCase().invoke(),
                textFieldValue = TextFieldValue(
                    text = "ברא",
                    selection = TextRange(index = 3),
                ),
                resultsSummary = "נמצאו 2 תוצאות".desc(),
                results = persistentListOf(
                    VerseUiModel(
                        id = "preview_1",
                        text = "בְּרֵאשִׁ֖ית בָּרָ֣א אֱלֹהִ֑ים אֵ֥ת הַשָּׁמַ֖יִם וְאֵ֥ת הָאָֽרֶץ",
                        source = "(בראשית א', א')",
                        highlightedRanges = persistentListOf(0..11),
                        bookIndex = 0,
                        chapterIndex = 1,
                        verse = "א",
                    ),
                    VerseUiModel(
                        id = "preview_2",
                        text = "וַיִּבְרָ֨א אֱלֹהִ֤ים אֶת־הָֽאָדָם֙ בְּצַלְמ֔וֹ",
                        source = "(בראשית א', כ\"ז)",
                        highlightedRanges = persistentListOf(0..10),
                        bookIndex = 0,
                        chapterIndex = 1,
                        verse = "א",
                    ),
                ),
            ),
            onAction = {},
            signsScrollState = rememberScrollState(),
        )
    }
}
